import {QueryResult} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {AppLifecycleState} from 'graphql-client/__generated/graphqlGlobalTypes';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import * as BenchlingAppActions from '../BenchlingAppActions';

interface BenchlingAppLifecycleButtonProps {
  appId: string;
  appLifecycleState: AppLifecycleState;
  pollForChangeComplete: (arg: String) => void;
  pollingStartTime: React.MutableRefObject<number>;
  name: string;
  appActivationViewable: boolean;
  refetch: QueryResult['refetch'];
  isLoading: boolean;
  missingWebhookUrl: boolean;
}

export default function BenchlingAppLifecycleButton(
  props: BenchlingAppLifecycleButtonProps
): JSX.Element | null {
  async function handleActivateBenchlingApp(): Promise<void> {
    props.pollingStartTime.current = new Date().getTime();
    const statusUrl = await BenchlingAppActions.activateApp(props.appId);
    props.pollForChangeComplete(statusUrl);
    await props.refetch();
  }

  async function handleDeactivateBenchlingApp(): Promise<void> {
    if (await BenchlingAppActions.confirmDeactivateApp()) {
      props.pollingStartTime.current = new Date().getTime();
      const statusUrl = await BenchlingAppActions.deactivateApp(props.appId);
      if (statusUrl !== '') {
        props.pollForChangeComplete(statusUrl);
      }
      await props.refetch();
    }
  }

  let button: JSX.Element;
  let icon: JSX.Element;
  let stateText: string;
  if (props.isLoading) {
    button = (
      <Button
        text=""
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        icon={<BenchlingIcon icon="refresh" className="glyphicon-spin" />}
        disabled={true}
        size={ButtonSize.SMALL}
        onClick={(): void => {}}
        textColor={Colors.GRAY}
      />
    );
    icon = <></>;
    stateText = '';
  } else {
    switch (props.appLifecycleState) {
      case AppLifecycleState.INACTIVE:
        button = (
          <Button
            dataTestAttrs={{component: 'BenchlingAppLifecycleButton', element: 'Button'}}
            text="Activate"
            buttonStyle={ButtonStyle.PRIMARY}
            icon={<></>}
            disabled={props.missingWebhookUrl}
            size={ButtonSize.SMALL}
            onClick={handleActivateBenchlingApp}
            textColor={Colors.WHITE}
          />
        );
        icon = <BenchlingIcon icon="dot" color={Colors.GRAY} />;
        stateText = 'Inactive';
        break;
      case AppLifecycleState.INSTALLED:
      case AppLifecycleState.DEACTIVATED:
        button = (
          <Button
            dataTestAttrs={{component: 'BenchlingAppLifecycleButton', element: 'Button'}}
            text="Activate"
            buttonStyle={ButtonStyle.PRIMARY}
            icon={<></>}
            disabled={props.missingWebhookUrl}
            size={ButtonSize.SMALL}
            onClick={handleActivateBenchlingApp}
            textColor={Colors.WHITE}
          />
        );
        icon = <BenchlingIcon icon="dot" color={Colors.GRAY} />;
        stateText = 'Inactive';
        break;
      case AppLifecycleState.ACTIVE:
        button = (
          <Button
            dataTestAttrs={{component: 'BenchlingAppLifecycleButton', element: 'Button'}}
            text="Deactivate"
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            icon={<></>}
            disabled={props.missingWebhookUrl}
            size={ButtonSize.SMALL}
            onClick={handleDeactivateBenchlingApp}
            textColor={Colors.RED}
          />
        );
        icon = <BenchlingIcon icon="done" color={Colors.GREEN} />;
        stateText = 'Active';
        break;
      case AppLifecycleState.ACTIVATE_PENDING:
        button = (
          <Button
            dataTestAttrs={{component: 'BenchlingAppLifecycleButton', element: 'Button'}}
            text="Activating"
            buttonStyle={ButtonStyle.PRIMARY}
            icon={<BenchlingIcon icon="refresh" className="glyphicon-spin" />}
            disabled={true}
            size={ButtonSize.SMALL}
            onClick={(): void => {}}
            textColor={Colors.WHITE}
          />
        );
        icon = <BenchlingIcon icon="dot" color={Colors.GRAY} />;
        stateText = 'Inactive';
        break;
      case AppLifecycleState.DEACTIVATE_PENDING:
        button = (
          <Button
            dataTestAttrs={{component: 'BenchlingAppLifecycleButton', element: 'Button'}}
            text="Deactivating"
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            icon={<BenchlingIcon icon="refresh" className="glyphicon-spin" />}
            disabled={true}
            size={ButtonSize.SMALL}
            onClick={(): void => {}}
            textColor={Colors.RED}
          />
        );
        icon = <BenchlingIcon icon="done" color={Colors.GREEN} />;
        stateText = 'Active';
        break;
      default:
        assertNever(props.appLifecycleState);
        button = <></>;
        icon = <></>;
        stateText = 'Inactive';
        break;
    }
  }
  const lifecycleState = (
    <div className={classNames(css(styles.appState))}>
      {icon} {stateText}
    </div>
  );

  return (
    <Header1 className={classNames('u-lr-split', 'u-spacing-top', css(styles.appTitle))}>
      <div>
        {props.name} &nbsp; &nbsp; {props.appActivationViewable && !props.isLoading && lifecycleState}
      </div>
      {(props.appActivationViewable || props.isLoading) && (
        <Tooltip
          title="App requires a Webhook URL to activate. Go to Feature Settings > Developer Console > Apps to add."
          disabled={!props.missingWebhookUrl}
          placement={TooltipPlacement.LEFT}
        >
          {button}
        </Tooltip>
      )}
    </Header1>
  );
}

const styles = StyleSheet.create({
  appTitle: {
    padding: '14px 12px 12px 14px',
    backgroundColor: `${Colors.WHITE}`,
    marginBottom: '0px',
    position: 'sticky',
    zIndex: 1,
    top: 0,
  },
  appState: {
    display: 'inline',
    fontSize: '13px',
  },
});
